/* eslint-disable no-undef */
import React, {useEffect, useState} from 'react';
import './ClosedAlarmsTable.scss'
import {
  DataTable,
  DataTableSkeleton,
  OverflowMenu,
  Pagination,
  SelectSkeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent
} from "@carbon/react";
import {Maximize} from '@carbon/react/icons';
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {
  errorState,
  loadingState,
  pageSizeState,
  pageState,
  rowsState,
  totalSizeState,
} from "../../state/closed_alerts_table";
import {grpcAlarmServiceSelector, grpcCustomerServiceSelector} from "../../state/grpc";
import AlarmExpandableContent from "./AlarmExpandableContent";
import {viewCustomerDetailModalCustomerCodeState, viewCustomerDetailModalOpenState} from "../../state/alerts_table";
import {useNavigate} from "react-router-dom";
import ComboBox from "@carbon/react/lib/components/ComboBox/ComboBox";

const ClosedAlarmsTable = () => {
  const headers = [{
    key: 'customerName', header: 'Cliente'
  }, {
    key: 'name', header: 'Nome'
  }, {
    key: 'instrumentName', header: 'Strumento'
  }, {
    key: 'closedAt', header: 'Chiuso il'
  }, {
    key: 'longDesc', header: 'Expanded'
  }];

  const navigate = useNavigate();
  const [loading, setLoading] = useRecoilState(loadingState)
  const [error, setError] = useRecoilState(errorState)
  const [initialRows, setRows] = useRecoilState(rowsState)
  const [page, setPage] = useRecoilState(pageState)
  const [pageSize, setPageSize] = useRecoilState(pageSizeState)
  const [totalSize, setTotalSize] = useRecoilState(totalSizeState)
  const setViewCustomerDetailModalOpen = useSetRecoilState(viewCustomerDetailModalOpenState)
  const setViewCustomerDetailModalCustomerCode = useSetRecoilState(viewCustomerDetailModalCustomerCodeState)
  const alarmsService = useRecoilValue(grpcAlarmServiceSelector)
  const systemService = useRecoilValue(grpcCustomerServiceSelector)
  const [loadingSystems, setLoadingSystems] = useState(true)
  const [systems, setSystems] = useState([])
  const [alarmsSystemFilter, setAlarmSystemFilter] = useState(null)

  async function getAlarmsTableData(page, pageSize) {
    const req = new proto.api.ListAlarmsRequest();
    const pageReq = new proto.api.PageRequest()
    pageReq.setPage(page)
    pageReq.setSize(pageSize)
    pageReq.setSort("updated_at desc")
    req.setStatus("Chiuso")
    if (alarmsSystemFilter != null) {
      req.setSystemCode(alarmsSystemFilter.id)
    }
    req.setPagination(pageReq)
    setLoading(true)
    alarmsService.listAlarms(req, null, (err, resp) => {
      if (err != null) {
        setError(true)
      } else {
        setRows(resp.toObject().alarmsList)
        setTotalSize(resp.toObject().pagination.total)
        setLoading(false)
      }
    })
  }

  function getSystems() {
    const req = new proto.api.ListCustomersRequest()
    systemService.listCustomers(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
      } else {
        setSystems(resp.toObject().customersList)
      }
      setLoadingSystems(false)
    })
  }

  useEffect(() => {
    getAlarmsTableData(page, pageSize)
  }, [page, pageSize, alarmsSystemFilter]);

  useEffect(() => {
    getSystems()
  }, [])

  if (loading) {
    return (<DataTableSkeleton
      columnCount={headers.length + 1}
      rowCount={5}
      headers={headers}
    />)
  }

  if (error) {
    return (<div>
      <span>ERROR!</span>
    </div>)
  }

  const processChildren = (alarm, cell) => {
    switch (cell.info.header) {
      case 'customerName': {
        return (
          <span className="customer-link" title="Dettagli cliente"
                onClick={() => {
                  setViewCustomerDetailModalOpen(true)
                  setViewCustomerDetailModalCustomerCode(alarm.customerCode)
                }}>
            {alarm.customerName}
          </span>
        )
      }
      case 'instrumentName': {
        return (
          <>
            {alarm.instrumentCode ? alarm.instrumentCode + ": " : ""}
            {alarm.instrumentName}
          </>
        )
      }
      default: {
        return (
          <>
            {cell.value}
          </>
        )
      }
    }
  }

  const handlePaginationChange = (page, newPageSize) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    setPage(page);
  }

  return (
    <>
      <DataTable
        rows={initialRows}
        headers={headers}
        overflowMenuOnHover={false}
        render={({
                   rows,
                   headers,
                   getHeaderProps,
                   getRowProps,
                   getTableProps,
                   getTableContainerProps,
                   getExpandHeaderProps,
                   getToolbarProps,
                 }) => {
          return (
            <TableContainer title="Allarmi chiusi"
                            description="Lista degli allarmi chiusi"
                            className="closed-alarms-table"
                            {...getTableContainerProps()}>
              <TableToolbar
                {...getToolbarProps()}>
                <TableToolbarContent>
                  {
                    loadingSystems ? <SelectSkeleton/> : <ComboBox
                      id="customer-code-filter"
                      size="lg"
                      placeholder="Filtra per sistema.."
                      style={{ width: "300px" }}
                      shouldFilterItem={(data) => {
                        if (data.inputValue == null || data.inputValue.length === 0) {
                          return true
                        }
                        return !data.itemToString().toLowerCase().includes(data.inputValue.toLowerCase())
                      }}
                      items={
                        systems.map(system => ({
                          id: system.code,
                          text: system.name
                        }))
                      }
                      selectedItem={alarmsSystemFilter}
                      itemToString={(item) => item == null ? "" : item.text}
                      itemToElement={(item) => item.text}
                      onChange={(el) => setAlarmSystemFilter(el.selectedItem)}/>
                  }
                </TableToolbarContent>
              </TableToolbar>
              <Pagination
                totalItems={totalSize}
                backwardText="Precedente"
                forwardText="Successiva"
                page={page}
                pageSize={pageSize}
                pageSizes={[10, 25, 50, 100]}
                itemsPerPageText="Elementi per pagina"
                pageRangeText={(_current, total) => `di ${total} ${total === 1 ? 'pagina' : 'pagine'}`}
                itemRangeText={(min, max, total) => `${min}–${max} of ${total} elementi`}
                onChange={({page, pageSize}) => handlePaginationChange(page, pageSize)}
              />
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableExpandHeader {...getExpandHeaderProps()} />
                    {headers.filter((header) => {
                      return header.key !== "longDesc"
                    }).map((header) => (
                      <TableHeader {...getHeaderProps({header})}>
                        {header.header}
                      </TableHeader>
                    ))}
                    <TableHeader scope="col"/>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    return {
                      alarm: initialRows.find(it => it.id === row.id),
                      ...row
                    }
                  }).map((row) => (<React.Fragment key={row.id}>
                    <TableExpandRow {...getRowProps({row})}>
                      {row.cells.filter((cell) => cell.info.header !== "longDesc").map((cell) => (
                        <TableCell key={cell.id}>{processChildren(row.alarm, cell)}</TableCell>))
                      }
                      <TableCell className="cds--table-column-menu">
                        <OverflowMenu flipped size='md' aria-label="Schermo intero" iconDescription="Schermo intero"
                                      renderIcon={Maximize}
                                      onClick={() => {
                                        navigate("/alarms/" + row.id)
                                      }}/>
                      </TableCell>
                    </TableExpandRow>
                    {row.isExpanded && (<TableExpandedRow colSpan={headers.length + 2}>
                      <AlarmExpandableContent alarm={initialRows.find(it => it.id === row.id)}
                                              onContentExpanded={() => {
                                              }}/>
                    </TableExpandedRow>)}
                  </React.Fragment>))
                  }
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      />
    </>
  )
}

export default ClosedAlarmsTable
